import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { v4 as uuidv4 } from 'uuid';
import { HexColorPicker } from 'react-colorful';
import {
  Type, Image, Square, Columns, Play, Link2, List, MessageSquare,
  Star, DollarSign, HelpCircle, BarChart3, ChevronDown, ChevronUp,
  Trash2, Copy, Eye, EyeOff, Settings, Palette, Layout, Box,
  Monitor, Tablet, Smartphone, Undo, Redo, Save, Download, Code,
  Plus, GripVertical, X, Check, Bold, Italic, Underline, AlignLeft,
  AlignCenter, AlignRight, ArrowLeft, Layers, Component, FileText,
  Grid3X3, MousePointer, Sparkles, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== COMPONENT DEFINITIONS ====================
const COMPONENT_LIBRARY = {
  layout: {
    name: 'Layout',
    icon: Layout,
    items: [
      { type: 'section', name: 'Section', icon: Square, defaultProps: { backgroundColor: '#ffffff', padding: '60px 20px' } },
      { type: 'container', name: 'Container', icon: Box, defaultProps: { maxWidth: '1200px', margin: '0 auto' } },
      { type: 'row', name: 'Row', icon: Columns, defaultProps: { display: 'flex', gap: '20px', flexWrap: 'wrap' } },
      { type: 'column', name: 'Column', icon: Grid3X3, defaultProps: { flex: '1', minWidth: '250px' } },
    ]
  },
  basic: {
    name: 'Basic',
    icon: Type,
    items: [
      { type: 'heading', name: 'Heading', icon: Type, defaultProps: { text: 'Heading Text', tag: 'h2', fontSize: '32px', fontWeight: '700' } },
      { type: 'text', name: 'Text Block', icon: FileText, defaultProps: { text: 'Your text content here...', fontSize: '16px', lineHeight: '1.6' } },
      { type: 'image', name: 'Image', icon: Image, defaultProps: { src: '', alt: 'Image', width: '100%', borderRadius: '8px' } },
      { type: 'button', name: 'Button', icon: MousePointer, defaultProps: { text: 'Click Me', backgroundColor: '#3b82f6', color: '#ffffff', padding: '12px 24px', borderRadius: '8px' } },
      { type: 'divider', name: 'Divider', icon: Square, defaultProps: { height: '1px', backgroundColor: '#e5e7eb', margin: '20px 0' } },
    ]
  },
  media: {
    name: 'Media',
    icon: Play,
    items: [
      { type: 'video', name: 'Video', icon: Play, defaultProps: { src: '', type: 'youtube' } },
      { type: 'icon', name: 'Icon', icon: Sparkles, defaultProps: { icon: 'star', size: '48px', color: '#3b82f6' } },
      { type: 'gallery', name: 'Gallery', icon: Image, defaultProps: { images: [], columns: 3 } },
    ]
  },
  content: {
    name: 'Content',
    icon: Layers,
    items: [
      { type: 'card', name: 'Card', icon: Square, defaultProps: { title: 'Card Title', description: 'Card description...', backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' } },
      { type: 'testimonial', name: 'Testimonial', icon: MessageSquare, defaultProps: { quote: 'Amazing service!', author: 'John Doe', role: 'CEO', rating: 5 } },
      { type: 'pricing', name: 'Pricing', icon: DollarSign, defaultProps: { title: 'Basic', price: '$99', period: '/month', features: ['Feature 1', 'Feature 2'] } },
      { type: 'faq', name: 'FAQ', icon: HelpCircle, defaultProps: { question: 'Question?', answer: 'Answer here...' } },
      { type: 'counter', name: 'Counter', icon: BarChart3, defaultProps: { number: '100+', label: 'Happy Clients' } },
      { type: 'list', name: 'List', icon: List, defaultProps: { items: ['Item 1', 'Item 2', 'Item 3'], style: 'bullet' } },
    ]
  },
  interactive: {
    name: 'Interactive',
    icon: Zap,
    items: [
      { type: 'accordion', name: 'Accordion', icon: ChevronDown, defaultProps: { items: [{ title: 'Section 1', content: 'Content...' }] } },
      { type: 'tabs', name: 'Tabs', icon: Columns, defaultProps: { tabs: [{ title: 'Tab 1', content: 'Content...' }] } },
      { type: 'form', name: 'Form', icon: FileText, defaultProps: { fields: [], submitText: 'Submit' } },
      { type: 'cta', name: 'CTA Section', icon: Sparkles, defaultProps: { title: 'Ready to Start?', description: 'Get in touch today!', buttonText: 'Contact Us', backgroundColor: 'linear-gradient(135deg, #3b82f6, #8b5cf6)' } },
    ]
  }
};

// ==================== STYLE PRESETS ====================
const FONT_OPTIONS = [
  'Inter', 'Manrope', 'Poppins', 'Roboto', 'Open Sans', 'Lato', 
  'Montserrat', 'Playfair Display', 'Raleway', 'Source Sans Pro'
];

const FONT_WEIGHTS = ['300', '400', '500', '600', '700', '800', '900'];

// ==================== SORTABLE ITEM COMPONENT ====================
function SortableElement({ element, isSelected, onSelect, onDelete, onDuplicate, renderElement }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
    >
      {/* Element Controls */}
      <div className={`absolute -top-8 left-0 right-0 flex items-center justify-between bg-blue-600 text-white text-xs px-2 py-1 rounded-t opacity-0 group-hover:opacity-100 transition-opacity z-10 ${isSelected ? 'opacity-100' : ''}`}>
        <div className="flex items-center gap-1">
          <button {...attributes} {...listeners} className="cursor-grab hover:bg-blue-700 p-1 rounded">
            <GripVertical size={12} />
          </button>
          <span className="font-medium">{element.type}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onDuplicate(element.id); }} className="hover:bg-blue-700 p-1 rounded">
            <Copy size={12} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(element.id); }} className="hover:bg-red-600 p-1 rounded">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      
      {/* Rendered Element */}
      {renderElement(element)}
    </div>
  );
}

// ==================== DRAGGABLE COMPONENT FROM LIBRARY ====================
function DraggableComponent({ item }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: `library-${item.type}`,
    data: { type: 'library', item }
  });

  const Icon = item.icon;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-2 p-2 rounded-lg cursor-grab hover:bg-slate-100 transition-colors ${isDragging ? 'opacity-50' : ''}`}
    >
      <Icon size={16} className="text-slate-500" />
      <span className="text-sm text-slate-700">{item.name}</span>
    </div>
  );
}

// ==================== STYLE PANEL COMPONENT ====================
function StylePanel({ element, onChange }) {
  const [activeTab, setActiveTab] = useState('style');
  const [showColorPicker, setShowColorPicker] = useState(null);

  if (!element) {
    return (
      <div className="p-4 text-center text-slate-500">
        <MousePointer size={48} className="mx-auto mb-3 text-slate-300" />
        <p className="text-sm">Select an element to edit its properties</p>
      </div>
    );
  }

  const updateStyle = (key, value) => {
    onChange({
      ...element,
      props: { ...element.props, [key]: value }
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {['style', 'advanced'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 text-sm font-medium capitalize ${activeTab === tab ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'style' && (
          <>
            {/* Typography */}
            {(element.type === 'heading' || element.type === 'text' || element.type === 'button') && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Typography</h4>
                
                {/* Text Content */}
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Content</label>
                  <textarea
                    value={element.props.text || ''}
                    onChange={(e) => updateStyle('text', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                    rows={3}
                  />
                </div>

                {/* Font Family */}
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Font Family</label>
                  <select
                    value={element.props.fontFamily || 'Inter'}
                    onChange={(e) => updateStyle('fontFamily', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    {FONT_OPTIONS.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                </div>

                {/* Font Size & Weight */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Size</label>
                    <input
                      type="text"
                      value={element.props.fontSize || '16px'}
                      onChange={(e) => updateStyle('fontSize', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Weight</label>
                    <select
                      value={element.props.fontWeight || '400'}
                      onChange={(e) => updateStyle('fontWeight', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      {FONT_WEIGHTS.map(w => (
                        <option key={w} value={w}>{w}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Line Height & Letter Spacing */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Line Height</label>
                    <input
                      type="text"
                      value={element.props.lineHeight || '1.5'}
                      onChange={(e) => updateStyle('lineHeight', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Letter Spacing</label>
                    <input
                      type="text"
                      value={element.props.letterSpacing || '0'}
                      onChange={(e) => updateStyle('letterSpacing', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {/* Text Align */}
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Alignment</label>
                  <div className="flex gap-1">
                    {[
                      { value: 'left', icon: AlignLeft },
                      { value: 'center', icon: AlignCenter },
                      { value: 'right', icon: AlignRight },
                    ].map(({ value, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => updateStyle('textAlign', value)}
                        className={`flex-1 p-2 rounded ${element.props.textAlign === value ? 'bg-blue-100 text-blue-600' : 'hover:bg-slate-100'}`}
                      >
                        <Icon size={16} className="mx-auto" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Colors */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Colors</h4>
              
              {/* Text Color */}
              <div>
                <label className="block text-xs text-slate-600 mb-1">Text Color</label>
                <div className="relative">
                  <button
                    onClick={() => setShowColorPicker(showColorPicker === 'color' ? null : 'color')}
                    className="w-full flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <div 
                      className="w-6 h-6 rounded border border-slate-200" 
                      style={{ backgroundColor: element.props.color || '#000000' }}
                    />
                    <span>{element.props.color || '#000000'}</span>
                  </button>
                  {showColorPicker === 'color' && (
                    <div className="absolute top-full left-0 mt-2 z-50 bg-white p-3 rounded-lg shadow-xl border">
                      <HexColorPicker
                        color={element.props.color || '#000000'}
                        onChange={(color) => updateStyle('color', color)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Background Color */}
              <div>
                <label className="block text-xs text-slate-600 mb-1">Background</label>
                <div className="relative">
                  <button
                    onClick={() => setShowColorPicker(showColorPicker === 'bg' ? null : 'bg')}
                    className="w-full flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <div 
                      className="w-6 h-6 rounded border border-slate-200" 
                      style={{ backgroundColor: element.props.backgroundColor || 'transparent' }}
                    />
                    <span>{element.props.backgroundColor || 'transparent'}</span>
                  </button>
                  {showColorPicker === 'bg' && (
                    <div className="absolute top-full left-0 mt-2 z-50 bg-white p-3 rounded-lg shadow-xl border">
                      <HexColorPicker
                        color={element.props.backgroundColor || '#ffffff'}
                        onChange={(color) => updateStyle('backgroundColor', color)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Spacing */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Spacing</h4>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Padding</label>
                  <input
                    type="text"
                    value={element.props.padding || '0'}
                    onChange={(e) => updateStyle('padding', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="10px 20px"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Margin</label>
                  <input
                    type="text"
                    value={element.props.margin || '0'}
                    onChange={(e) => updateStyle('margin', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="10px 20px"
                  />
                </div>
              </div>
            </div>

            {/* Border */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Border</h4>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Border Radius</label>
                  <input
                    type="text"
                    value={element.props.borderRadius || '0'}
                    onChange={(e) => updateStyle('borderRadius', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Border Width</label>
                  <input
                    type="text"
                    value={element.props.borderWidth || '0'}
                    onChange={(e) => updateStyle('borderWidth', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Shadow */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Effects</h4>
              
              <div>
                <label className="block text-xs text-slate-600 mb-1">Box Shadow</label>
                <input
                  type="text"
                  value={element.props.boxShadow || 'none'}
                  onChange={(e) => updateStyle('boxShadow', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="0 4px 6px rgba(0,0,0,0.1)"
                />
              </div>
            </div>

            {/* Image specific */}
            {element.type === 'image' && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Image</h4>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Image URL</label>
                  <input
                    type="text"
                    value={element.props.src || ''}
                    onChange={(e) => updateStyle('src', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Alt Text</label>
                  <input
                    type="text"
                    value={element.props.alt || ''}
                    onChange={(e) => updateStyle('alt', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'advanced' && (
          <div className="space-y-4">
            {/* Custom CSS */}
            <div>
              <label className="block text-xs text-slate-600 mb-1">Custom CSS</label>
              <textarea
                value={element.props.customCSS || ''}
                onChange={(e) => updateStyle('customCSS', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
                rows={6}
                placeholder=".element { ... }"
              />
            </div>

            {/* CSS Classes */}
            <div>
              <label className="block text-xs text-slate-600 mb-1">CSS Classes</label>
              <input
                type="text"
                value={element.props.className || ''}
                onChange={(e) => updateStyle('className', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="my-class another-class"
              />
            </div>

            {/* Element ID */}
            <div>
              <label className="block text-xs text-slate-600 mb-1">Element ID</label>
              <input
                type="text"
                value={element.props.elementId || ''}
                onChange={(e) => updateStyle('elementId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="my-element"
              />
            </div>

            {/* Visibility per device */}
            <div>
              <label className="block text-xs text-slate-600 mb-2">Visibility</label>
              <div className="space-y-2">
                {[
                  { key: 'hideOnDesktop', label: 'Hide on Desktop', icon: Monitor },
                  { key: 'hideOnTablet', label: 'Hide on Tablet', icon: Tablet },
                  { key: 'hideOnMobile', label: 'Hide on Mobile', icon: Smartphone },
                ].map(({ key, label, icon: Icon }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={element.props[key] || false}
                      onChange={(e) => updateStyle(key, e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <Icon size={14} />
                    <span className="text-sm text-slate-600">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN PAGE BUILDER COMPONENT ====================
export default function PageBuilder() {
  const navigate = useNavigate();
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [activeDevice, setActiveDevice] = useState('desktop');
  const [showPreview, setShowPreview] = useState(false);
  const [pageName, setPageName] = useState('Untitled Page');
  const [pageSlug, setPageSlug] = useState('untitled-page');
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['layout', 'basic']);
  const [activePanel, setActivePanel] = useState('components');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Save to history
  const saveToHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newElements);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(history[historyIndex - 1]);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(history[historyIndex + 1]);
    }
  }, [history, historyIndex]);

  // Add element
  const addElement = useCallback((item) => {
    const newElement = {
      id: uuidv4(),
      type: item.type,
      props: { ...item.defaultProps },
      children: []
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setSelectedElement(newElement.id);
  }, [elements, saveToHistory]);

  // Delete element
  const deleteElement = useCallback((id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    saveToHistory(newElements);
    if (selectedElement === id) setSelectedElement(null);
  }, [elements, selectedElement, saveToHistory]);

  // Duplicate element
  const duplicateElement = useCallback((id) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      const newElement = {
        ...element,
        id: uuidv4(),
        props: { ...element.props }
      };
      const index = elements.findIndex(el => el.id === id);
      const newElements = [...elements];
      newElements.splice(index + 1, 0, newElement);
      setElements(newElements);
      saveToHistory(newElements);
      setSelectedElement(newElement.id);
    }
  }, [elements, saveToHistory]);

  // Update element
  const updateElement = useCallback((updatedElement) => {
    const newElements = elements.map(el => 
      el.id === updatedElement.id ? updatedElement : el
    );
    setElements(newElements);
    saveToHistory(newElements);
  }, [elements, saveToHistory]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) return;

    // If dragging from library
    if (active.data.current?.type === 'library') {
      addElement(active.data.current.item);
      return;
    }

    // Reordering existing elements
    if (active.id !== over.id) {
      const oldIndex = elements.findIndex(el => el.id === active.id);
      const newIndex = elements.findIndex(el => el.id === over.id);
      const newElements = arrayMove(elements, oldIndex, newIndex);
      setElements(newElements);
      saveToHistory(newElements);
    }
  };

  // Render element
  const renderElement = (element) => {
    const style = {
      fontFamily: element.props.fontFamily,
      fontSize: element.props.fontSize,
      fontWeight: element.props.fontWeight,
      lineHeight: element.props.lineHeight,
      letterSpacing: element.props.letterSpacing,
      textAlign: element.props.textAlign,
      color: element.props.color,
      backgroundColor: element.props.backgroundColor,
      padding: element.props.padding,
      margin: element.props.margin,
      borderRadius: element.props.borderRadius,
      borderWidth: element.props.borderWidth,
      borderStyle: element.props.borderWidth ? 'solid' : undefined,
      borderColor: element.props.borderColor,
      boxShadow: element.props.boxShadow,
      width: element.props.width,
      maxWidth: element.props.maxWidth,
      display: element.props.display,
      flexDirection: element.props.flexDirection,
      gap: element.props.gap,
      flexWrap: element.props.flexWrap,
      flex: element.props.flex,
      minWidth: element.props.minWidth,
    };

    switch (element.type) {
      case 'heading':
        const HeadingTag = element.props.tag || 'h2';
        return <HeadingTag style={style}>{element.props.text}</HeadingTag>;
      
      case 'text':
        return <p style={style}>{element.props.text}</p>;
      
      case 'button':
        return (
          <button style={{ ...style, border: 'none', cursor: 'pointer' }}>
            {element.props.text}
          </button>
        );
      
      case 'image':
        return element.props.src ? (
          <img src={element.props.src} alt={element.props.alt} style={style} />
        ) : (
          <div style={{ ...style, backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
            <Image size={48} className="text-slate-300" />
          </div>
        );
      
      case 'divider':
        return <hr style={style} />;
      
      case 'section':
        return <div style={{ ...style, minHeight: '100px' }}>{element.props.placeholder || 'Section'}</div>;
      
      case 'container':
        return <div style={style}>{element.props.placeholder || 'Container'}</div>;
      
      case 'row':
        return <div style={style}>{element.props.placeholder || 'Row (Flex)'}</div>;
      
      case 'column':
        return <div style={style}>{element.props.placeholder || 'Column'}</div>;
      
      case 'card':
        return (
          <div style={style} className="p-6">
            <h3 className="text-xl font-bold mb-2">{element.props.title}</h3>
            <p className="text-slate-600">{element.props.description}</p>
          </div>
        );
      
      case 'testimonial':
        return (
          <div style={style} className="p-6 text-center">
            <div className="flex justify-center mb-3">
              {[...Array(element.props.rating || 5)].map((_, i) => (
                <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-lg italic mb-4">"{element.props.quote}"</p>
            <p className="font-bold">{element.props.author}</p>
            <p className="text-sm text-slate-500">{element.props.role}</p>
          </div>
        );
      
      case 'cta':
        return (
          <div style={{ ...style, background: element.props.backgroundColor }} className="p-12 text-center text-white rounded-xl">
            <h2 className="text-3xl font-bold mb-3">{element.props.title}</h2>
            <p className="text-lg mb-6 opacity-90">{element.props.description}</p>
            <button className="px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold">
              {element.props.buttonText}
            </button>
          </div>
        );
      
      case 'counter':
        return (
          <div style={style} className="text-center p-6">
            <div className="text-4xl font-bold text-blue-600 mb-2">{element.props.number}</div>
            <div className="text-slate-600">{element.props.label}</div>
          </div>
        );
      
      case 'faq':
        return (
          <div style={style} className="border border-slate-200 rounded-lg p-4">
            <h4 className="font-bold mb-2">{element.props.question}</h4>
            <p className="text-slate-600">{element.props.answer}</p>
          </div>
        );
      
      default:
        return <div style={style}>{element.type}</div>;
    }
  };

  // Save page
  const savePage = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const pageData = {
        title: pageName,
        slug: pageSlug,
        elements: elements,
        device_styles: {},
        meta: {
          title: pageName,
          description: '',
        }
      };

      await axios.post(`${BACKEND_URL}/api/admin/page-builder`, pageData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Page saved successfully!');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  // Export JSON
  const exportJSON = () => {
    const dataStr = JSON.stringify({ pageName, pageSlug, elements }, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${pageSlug}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const deviceWidths = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px'
  };

  const selectedEl = elements.find(el => el.id === selectedElement);

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      {/* Top Toolbar */}
      <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back</span>
          </button>
          <div className="h-6 w-px bg-slate-200" />
          <input
            type="text"
            value={pageName}
            onChange={(e) => {
              setPageName(e.target.value);
              setPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
            }}
            className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Device Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            {[
              { key: 'desktop', icon: Monitor },
              { key: 'tablet', icon: Tablet },
              { key: 'mobile', icon: Smartphone },
            ].map(({ key, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveDevice(key)}
                className={`p-2 rounded ${activeDevice === key ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>

          <div className="h-6 w-px bg-slate-200" />

          {/* Undo/Redo */}
          <button
            onClick={undo}
            disabled={historyIndex <= 0}
            className="p-2 rounded hover:bg-slate-100 disabled:opacity-30"
          >
            <Undo size={18} />
          </button>
          <button
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="p-2 rounded hover:bg-slate-100 disabled:opacity-30"
          >
            <Redo size={18} />
          </button>

          <div className="h-6 w-px bg-slate-200" />

          {/* Preview */}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`p-2 rounded ${showPreview ? 'bg-blue-100 text-blue-600' : 'hover:bg-slate-100'}`}
          >
            <Eye size={18} />
          </button>

          {/* Export */}
          <button
            onClick={exportJSON}
            className="p-2 rounded hover:bg-slate-100"
            title="Export JSON"
          >
            <Download size={18} />
          </button>

          {/* Save */}
          <button
            onClick={savePage}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save size={18} />
            <span>{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* Left Panel - Components Library */}
          <div className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0">
            {/* Panel Tabs */}
            <div className="flex border-b border-slate-200">
              {[
                { key: 'components', label: 'Components', icon: Component },
                { key: 'layers', label: 'Layers', icon: Layers },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActivePanel(key)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium ${activePanel === key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-3">
              {activePanel === 'components' && (
                <div className="space-y-2">
                  {Object.entries(COMPONENT_LIBRARY).map(([key, category]) => {
                    const Icon = category.icon;
                    const isExpanded = expandedCategories.includes(key);
                    return (
                      <div key={key} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedCategories(
                            isExpanded 
                              ? expandedCategories.filter(c => c !== key)
                              : [...expandedCategories, key]
                          )}
                          className="w-full flex items-center justify-between p-3 hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-2">
                            <Icon size={16} className="text-slate-500" />
                            <span className="font-medium text-sm">{category.name}</span>
                          </div>
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        {isExpanded && (
                          <div className="p-2 pt-0 space-y-1">
                            {category.items.map((item) => (
                              <div
                                key={item.type}
                                onClick={() => addElement(item)}
                                className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                              >
                                <item.icon size={16} className="text-slate-500" />
                                <span className="text-sm text-slate-700">{item.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {activePanel === 'layers' && (
                <div className="space-y-1">
                  {elements.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No elements yet</p>
                  ) : (
                    elements.map((element, index) => (
                      <div
                        key={element.id}
                        onClick={() => setSelectedElement(element.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${selectedElement === element.id ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-100'}`}
                      >
                        <GripVertical size={14} className="text-slate-400" />
                        <span className="text-sm capitalize">{element.type}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 overflow-auto p-8 bg-slate-100">
            <div 
              className="mx-auto bg-white shadow-lg rounded-lg min-h-[600px] transition-all duration-300"
              style={{ width: deviceWidths[activeDevice], maxWidth: '100%' }}
              onClick={() => setSelectedElement(null)}
            >
              {showPreview ? (
                // Preview Mode
                <div className="p-4">
                  {elements.map((element) => (
                    <div key={element.id}>
                      {renderElement(element)}
                    </div>
                  ))}
                </div>
              ) : (
                // Edit Mode
                <SortableContext items={elements.map(el => el.id)} strategy={verticalListSortingStrategy}>
                  <div className="p-4 min-h-[600px]">
                    {elements.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-slate-200 rounded-lg">
                        <Plus size={48} className="text-slate-300 mb-4" />
                        <p className="text-slate-500 mb-2">Start building your page</p>
                        <p className="text-sm text-slate-400">Click components on the left to add them</p>
                      </div>
                    ) : (
                      elements.map((element) => (
                        <SortableElement
                          key={element.id}
                          element={element}
                          isSelected={selectedElement === element.id}
                          onSelect={setSelectedElement}
                          onDelete={deleteElement}
                          onDuplicate={duplicateElement}
                          renderElement={renderElement}
                        />
                      ))
                    )}
                  </div>
                </SortableContext>
              )}
            </div>
          </div>
        </DndContext>

        {/* Right Panel - Style Controls */}
        <div className="w-80 bg-white border-l border-slate-200 shrink-0">
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Properties</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              <StylePanel
                element={selectedEl}
                onChange={updateElement}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
